// models/ReportTypeDesignHelper.js
// Centralized helpers for report type design (formatting + classes)

// Words that should be fully uppercased when displayed
const ACRONYMS = new Set(['ecg', 'ett', 'eto']);

// Common articles/prepositions (fr, de, en) to keep lowercase even if short
const STOPWORDS = new Set([
  // French
  'le', 'la', 'les', 'un', 'une', 'des', 'de', 'du', 'au', 'aux', 'et', 'ou', 'en', 'dans', 'sur', 'par', 'pour', 'avec', 'sans', 'sous',
  // German
  'der', 'die', 'das', 'ein', 'eine', 'und', 'zu', 'im', 'am', 'vom', 'aus', 'mit', 'ohne', 'an', 'auf', 'bei', 'von', 'in',
  // English
  'the', 'and', 'for', 'of', 'to', 'in', 'on', 'at', 'by', 'as', 'an', 'a', 'or'
]);

function normalizeTypeName(raw, allowSpaces = false) {
  if (raw == null) return '';
  const base = allowSpaces ? String(raw) : String(raw).trim();
  // If string is empty or only whitespace, consider it empty
  if ((!allowSpaces && !base) || (allowSpaces && base.trim().length === 0)) return '';
  const replaced = allowSpaces
    ? base.replace(/_/g, ' ')
    : base.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  return replaced.toLowerCase();
}

// Main formatting function with enhanced logic from reportTypeDesign.js
export function formatDisplayName(typeName, allowSpaces = false) {
  const normalized = normalizeTypeName(typeName, allowSpaces);
  if (!normalized) return '';

  if (allowSpaces) {
    // Preserve original spacing by splitting and keeping whitespace tokens
    const tokens = normalized.split(/(\s+)/);
    const mapped = tokens.map((token) => {
      if (!token) return token;
      if (/^\s+$/.test(token)) return token; // keep spaces as-is
      if (ACRONYMS.has(token)) return token.toUpperCase();
      if (token.length <= 3) {
        return STOPWORDS.has(token) ? token : token.toUpperCase();
      }
      return token;
    });
    const body = mapped.join('');
    return body.charAt(0).toUpperCase() + body.slice(1);
  }

  const tokens = normalized.split(' ');
  const mapped = tokens.map((token) => {
    if (!token) return token;
    if (ACRONYMS.has(token)) return token.toUpperCase();
    if (token.length <= 3) {
      return STOPWORDS.has(token) ? token : token.toUpperCase();
    }
    return token;
  });

  const body = mapped.join(' ');
  return body.charAt(0).toUpperCase() + body.slice(1);
}

// Alias for backward compatibility
export const formatReportTypeDisplayName = formatDisplayName;

function matchKeywordClass(lower, mapping) {
  for (const { keyword, cls } of mapping) {
    if (lower.includes(keyword)) return cls;
  }
  return null;
}

export function getButtonClass(typeName) {
  if (!typeName || typeof typeName !== 'string') return 'button-info';
  const lower = String(typeName).toLowerCase();
  const map = [
    { keyword: 'consultation', cls: 'button-blue' },
    { keyword: 'note', cls: 'button-yellow' },
    { keyword: 'lettre', cls: 'button-green' },
    { keyword: 'texte', cls: 'button-orange' },
    { keyword: 'mail', cls: 'button-cyan' },
    { keyword: 'traduction', cls: 'button-pink' },
    { keyword: 'examen', cls: 'button-purple' },
    { keyword: 'liste', cls: 'button-cyan' },
    { keyword: 'diagnostic', cls: 'button-mint' },
    { keyword: 'transmission', cls: 'button-indigo' },
    { keyword: 'entree', cls: 'button-brown' },
    { keyword: 'synthèse', cls: 'button-pink' },
    { keyword: 'echocardiographie', cls: 'button-pink' },
    { keyword: 'ergometrie', cls: 'button-pink' },
    { keyword: 'coronarographie', cls: 'button-pink' },
    { keyword: 'ecg', cls: 'button-pink' },
    { keyword: 'inconnu', cls: 'button-red' },
  ];
  return matchKeywordClass(lower, map) || 'button-info';
}

// Alias for backward compatibility
export const getReportTypeButtonClass = getButtonClass;

export function getIconClass(typeName) {
  if (!typeName || typeof typeName !== 'string') return 'icon-default';
  const lower = String(typeName).toLowerCase();
  const map = [
    { keyword: 'consultation', cls: 'icon-consultation' },
    { keyword: 'note', cls: 'icon-note' },
    { keyword: 'lettre', cls: 'icon-lettre' },
    { keyword: 'texte', cls: 'icon-texte' },
    { keyword: 'mail', cls: 'icon-mail' },
    { keyword: 'traduction', cls: 'icon-traduction' },
    { keyword: 'examen', cls: 'icon-examen' },
    { keyword: 'liste', cls: 'icon-liste' },
    { keyword: 'diagnostic', cls: 'icon-diagnostic' },
    { keyword: 'transmission', cls: 'icon-transmission' },
    { keyword: 'entree', cls: 'icon-entree' },
    { keyword: 'synthèse', cls: 'icon-ecg' },
    { keyword: 'echocardiographie', cls: 'icon-ecg' },
    { keyword: 'ergometrie', cls: 'icon-ecg' },
    { keyword: 'coronarographie', cls: 'icon-ecg' },
    { keyword: 'ecg', cls: 'icon-ecg' },
  ];
  return matchKeywordClass(lower, map) || 'icon-default';
}

// Alias for backward compatibility
export const getReportTypeIconClass = getIconClass;

export function getIconLetter(typeName) {
  const display = formatDisplayName(typeName);
  if (!display) return '?';
  return display.charAt(0).toUpperCase();
}

// Alias for backward compatibility
export const getReportTypeIconLetter = getIconLetter;

// Convenience helper to compute all UI props for a report type card
export function getCardDesign(typeName) {
  return {
    displayName: formatDisplayName(typeName),
    iconClass: getIconClass(typeName),
    buttonClass: getButtonClass(typeName),
    iconLetter: getIconLetter(typeName),
  };
}

// Alias for backward compatibility
export const getReportTypeCardDesign = getCardDesign;